// NEW: src/hooks/use-audio-player.js
//
// Per-track audio player hook. One HTMLAudioElement per src, lifecycle tied
// to the component. Respects the global sound manager's mute flag so the
// Settings > Sounds toggle silences this too.
//
// Safe to call with a falsy src — the hook returns a disabled state and
// never creates an <Audio>.

import { useEffect, useRef, useState } from "react";
import soundManager from "../sounds/sound-manager";

export function useAudioPlayer(src) {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    if (!src) return undefined;

    setIsLoaded(false);
    setIsPlaying(false);

    const audio = new Audio(src);
    audioRef.current = audio;

    const onLoad = () => setIsLoaded(true);
    const onEnd = () => setIsPlaying(false);
    const onError = () => { setIsLoaded(false); setIsPlaying(false); };

    audio.addEventListener("canplaythrough", onLoad);
    audio.addEventListener("ended", onEnd);
    audio.addEventListener("error", onError);
    // Some browsers fire loadedmetadata earlier than canplaythrough, and a few
    // mobile WebViews never fire canplaythrough without a user gesture.
    // Listening to loadedmetadata too means the button unblocks reliably.
    audio.addEventListener("loadedmetadata", onLoad);
    audio.load();

    return () => {
      try { audio.pause(); } catch { /* no-op */ }
      audio.removeEventListener("canplaythrough", onLoad);
      audio.removeEventListener("loadedmetadata", onLoad);
      audio.removeEventListener("ended", onEnd);
      audio.removeEventListener("error", onError);
      audioRef.current = null;
    };
  }, [src]);

  const toggle = () => {
    // Respect the app-wide mute toggle set in Settings > Sounds
    if (soundManager.isMuted()) return;
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) {
      try { audio.pause(); } catch { /* no-op */ }
      setIsPlaying(false);
    } else {
      try { audio.currentTime = 0; } catch { /* no-op */ }
      const p = audio.play();
      if (p && typeof p.then === "function") {
        p.then(() => setIsPlaying(true)).catch(() => setIsPlaying(false));
      } else {
        setIsPlaying(true);
      }
    }
  };

  return { isPlaying, isLoaded, toggle };
}

export default useAudioPlayer;
